import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { 
  BookOpen, Clock, Star, AlertTriangle, BookText, BrainCircuit, Loader2, 
  FileText, Download, BarChart, Gauge, Users, Trophy, ArrowUpRight, Brackets, Info
} from "lucide-react";
import { categories } from "@/types/learning-path";
import { useLearningPath } from "@/hooks/useLearningPath";
import { useAdaptiveLearning } from "@/hooks/useAdaptiveLearning";
import { CategoryCard } from "./CategoryCard";
import { Button } from "@/components/ui/button";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import MathRenderer from "@/components/mock-test/MathRenderer";

export interface LearningPathVisualizationProps {}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  trend?: number;
  color: string;
}

const StatCard = ({ title, value, icon, description, trend, color }: StatCardProps) => {
  const { isMobile } = useIsMobile();
  
  return (
    <Card className={`p-3 sm:p-4 border ${color} hover:shadow-md transition-all duration-300 w-full`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <h3 className="text-xl sm:text-2xl font-bold mt-1">{value}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
        <div className={`p-2 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
      {trend !== undefined && (
        <div className="flex items-center mt-2">
          <span className={trend >= 0 ? "text-green-500" : "text-red-500"}>
            {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
          <span className="text-xs text-muted-foreground ml-1">from last week</span>
        </div>
      )}
    </Card>
  );
};

const LearningPathVisualization: React.FC<LearningPathVisualizationProps> = () => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const { getTopicStatus, isLoadingPerformance, overallProgress, recentPerformance } = useLearningPath();
  const { 
    getRecommendedTopics, 
    getRecommendedDifficulty, 
    getNextReviewDate 
  } = useAdaptiveLearning();
  const { isMobile } = useIsMobile();

  const [showLectureDialog, setShowLectureDialog] = useState(false);
  const [showInsightsDialog, setShowInsightsDialog] = useState(false);
  const [topicInput, setTopicInput] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeDialog, setActiveDialog] = useState<'lecture' | 'insights' | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");

  const recommendedTopics = getRecommendedTopics();

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };

  const isRecommended = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category && recommendedTopics.includes(category.subject || '');
  };

  const getReviewStatus = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return null;

    const reviewDate = getNextReviewDate(category);
    if (!reviewDate) return null;

    const now = new Date();
    const isOverdue = reviewDate < now;
    const isDueToday = reviewDate.toDateString() === now.toDateString();

    if (isOverdue) {
      return { status: 'overdue', date: reviewDate };
    } else if (isDueToday) {
      return { status: 'today', date: reviewDate };
    } else {
      return { status: 'scheduled', date: reviewDate };
    }
  };

  const handleGenerateContent = async (type: 'lecture' | 'insights') => {
    if (!topicInput.trim()) {
      toast.error("Please enter a topic first");
      return;
    }

    setIsGenerating(true);
    setGeneratedContent("");
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-learning-content', {
        body: {
          topic: topicInput,
          contentType: type
        }
      });
      
      if (error) throw error;
      
      setGeneratedContent(data.content);
      toast.success(`${type === 'lecture' ? 'Lecture' : 'Insights'} generated successfully using ${data.provider}!`);
    } catch (error) {
      console.error(`Error generating ${type}:`, error);
      toast.error(`Failed to generate ${type}. Please try again.`);
    } finally {
      setIsGenerating(false);
    }
  };

  const openDialog = (type: 'lecture' | 'insights') => {
    setActiveDialog(type);
    setTopicInput("");
    setGeneratedContent("");
    if (type === 'lecture') {
      setShowLectureDialog(true);
    } else {
      setShowInsightsDialog(true);
    }
  };

  const downloadAsWord = () => {
    try {
      if (!generatedContent) {
        toast.error("No content to download");
        return;
      }
      
      const htmlContent = formatLearningContent(generatedContent);
      const blob = new Blob([`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${topicInput}</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
            .main-heading { color: #6B46C1; font-size: 24px; font-weight: bold; margin: 20px 0 10px 0; }
            .sub-heading { color: #059669; font-size: 20px; font-weight: bold; margin: 15px 0 8px 0; }
            .section-heading { color: #DC2626; font-size: 18px; font-weight: bold; margin: 12px 0 6px 0; }
            .section-header { color: #1E40AF; font-size: 16px; font-weight: bold; margin: 10px 0 5px 0; }
            .numbered-item { margin: 10px 0; }
            .bullet-list { margin: 10px 0; }
            .math-content { background: #F3F4F6; padding: 10px; border-radius: 4px; font-family: monospace; }
            .emphasized-text { background: #FEF3C7; padding: 5px; border-radius: 4px; }
            .special-note { background: #DBEAFE; padding: 10px; border-radius: 4px; border-left: 4px solid #3B82F6; }
            .bold { font-weight: bold; }
            .italic { font-style: italic; }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `], { type: 'text/html' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${topicInput.replace(/\s+/g, '_')}_${activeDialog}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      toast.success("Downloaded as HTML document");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download document");
    }
  };
  
  const downloadAsPdf = () => {
    try {
      if (!generatedContent) {
        toast.error("No content to download");
        return;
      }
      
      const htmlContent = formatLearningContent(generatedContent);
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>${topicInput}</title>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
              .main-heading { color: #6B46C1; font-size: 24px; font-weight: bold; margin: 20px 0 10px 0; }
              .sub-heading { color: #059669; font-size: 20px; font-weight: bold; margin: 15px 0 8px 0; }
              .section-heading { color: #DC2626; font-size: 18px; font-weight: bold; margin: 12px 0 6px 0; }
              .section-header { color: #1E40AF; font-size: 16px; font-weight: bold; margin: 10px 0 5px 0; }
              .numbered-item { margin: 10px 0; }
              .bullet-list { margin: 10px 0; }
              .math-content { background: #F3F4F6; padding: 10px; border-radius: 4px; font-family: monospace; }
              .emphasized-text { background: #FEF3C7; padding: 5px; border-radius: 4px; }
              .special-note { background: #DBEAFE; padding: 10px; border-radius: 4px; border-left: 4px solid #3B82F6; }
              .bold { font-weight: bold; }
              .italic { font-style: italic; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
        printWindow.close();
      }
      
      toast.success("Print dialog opened for PDF save");
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to open print dialog");
    }
  };

  const completedTopics = categories.reduce((acc, category) => {
    let count = 0;
    if (getTopicStatus(category) === "completed") count++;
    
    if (category.subcategories) {
      category.subcategories.forEach(sub => {
        if (getTopicStatus(sub) === "completed") count++;
      });
    }
    
    return acc + count;
  }, 0);
  
  const inProgressTopics = categories.reduce((acc, category) => {
    let count = 0;
    if (getTopicStatus(category) === "in-progress") count++;
    
    if (category.subcategories) {
      category.subcategories.forEach(sub => {
        if (getTopicStatus(sub) === "in-progress") count++;
      });
    }
    
    return acc + count;
  }, 0);

  const avgRecentScore = recentPerformance.length > 0 
    ? Math.round(recentPerformance.reduce((acc, curr) => acc + curr.score, 0) / recentPerformance.length) 
    : 0;

  const handleGenerateFromSubject = (topic: string, type: 'lecture' | 'insights') => {
    setTopicInput(topic);
    if (type === 'lecture') {
      setShowLectureDialog(true);
    } else {
      setShowInsightsDialog(true);
    }
  };

  const formatLearningContent = (text: string) => {
    if (!text) return '';
    
    // Convert markdown formatting to HTML with colored headings
    let cleanedText = text
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/\\n/g, '\n')
      // Handle bold and italic markdown
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .trim();

    // Split into sections and format with colored headings
    const sections = cleanedText.split(/\n\n+/).filter(section => section.trim());
    
    let formattedContent = '';
    
    for (let section of sections) {
      const trimmedSection = section.trim();
      if (!trimmedSection) continue;
      
      // Handle main headings (# or ###)  
      if (trimmedSection.match(/^#{1,3}\s+/)) {
        const level = (trimmedSection.match(/^#+/) || [''])[0].length;
        let content = trimmedSection.replace(/^#+\s*/, '');
        
        const className = level === 1 ? 'main-heading' : level === 2 ? 'sub-heading' : 'section-heading';
        formattedContent += `<div class="${className}">${content}</div>\n`;
        continue;
      }
      
      // Handle section headers with special keywords
      if (trimmedSection.match(/^(introduction|overview|key concepts|examples|applications|summary|conclusion|learning objectives|prerequisites|definition|types|operations|properties)/i)) {
        formattedContent += `<div class="section-header">${trimmedSection}</div>\n`;
        continue;
      }
      
      // Handle numbered lists
      const numberedMatch = trimmedSection.match(/^(\d+)\.\s*(.+)/s);
      if (numberedMatch) {
        const number = parseInt(numberedMatch[1]);
        let content = numberedMatch[2].trim();
        formattedContent += `<div class="numbered-item"><strong>${number}.</strong> ${content}</div>\n`;
        continue;
      }
      
      // Handle bullet points
      if (trimmedSection.includes('\n- ') || trimmedSection.startsWith('- ')) {
        const points = trimmedSection.split(/\n- /).map(point => {
          let cleanPoint = point.replace(/^- /, '').trim();
          return cleanPoint;
        }).filter(point => point);
        
        formattedContent += '<div class="bullet-list"><ul>\n';
        points.forEach(point => {
          formattedContent += `<li>${point}</li>\n`;
        });
        formattedContent += '</ul></div>\n';
        continue;
      }
      
      // Handle mathematical expressions
      if (trimmedSection.includes('\\(') || trimmedSection.includes('\\[') || 
          trimmedSection.includes('matrix') || trimmedSection.includes('vec') ||
          (trimmedSection.includes('=') && trimmedSection.includes('('))) {
        formattedContent += `<div class="math-content">${trimmedSection}</div>\n`;
        continue;
      }
      
      // Handle emphasized text (contains bold)
      if (trimmedSection.includes('<strong>')) {
        formattedContent += `<div class="emphasized-text">${trimmedSection}</div>\n`;
        continue;
      }
      
      // Handle special notes
      const isSpecialNote = trimmedSection.toLowerCase().includes('note:') ||
                           trimmedSection.toLowerCase().includes('important:') ||
                           trimmedSection.toLowerCase().includes('remember:') ||
                           trimmedSection.toLowerCase().includes('tip:');
      
      if (isSpecialNote) {
        formattedContent += `<div class="special-note">${trimmedSection}</div>\n`;
      } else {
        formattedContent += `<p>${trimmedSection}</p>\n`;
      }
    }
    
    return formattedContent;
  };

  const renderLearningContent = (content: string) => {
    // Clean content to remove markdown artifacts and format properly
    const cleanContent = content
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/\*\*/g, '') // Remove all ** characters
      .replace(/\*/g, '') // Remove all * characters
      .trim();
    
    // Split content into lines for processing
    const lines = cleanContent.split('\n').filter(line => line.trim());
    
    return (
      <div className="space-y-4">
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          
          // Handle main headings (# Vector & Matrices)
          if (trimmedLine.match(/^# /)) {
            const title = trimmedLine.replace(/^# /, '');
            return (
              <div key={index} className="mb-6">
                <h1 className="text-3xl font-bold text-purple-700 mb-4 pb-2 border-b-2 border-purple-200">
                  <MathRenderer text={title} />
                </h1>
              </div>
            );
          }
          
          // Handle sub-headings (## Introduction to the Topic)
          if (trimmedLine.match(/^## /)) {
            const title = trimmedLine.replace(/^## /, '');
            return (
              <div key={index} className="mb-5">
                <h2 className="text-2xl font-semibold text-blue-600 mb-3">
                  <MathRenderer text={title} />
                </h2>
              </div>
            );
          }
          
          // Handle section headings (### Key Concepts)
          if (trimmedLine.match(/^### /)) {
            const title = trimmedLine.replace(/^### /, '');
            return (
              <div key={index} className="mb-4">
                <h3 className="text-xl font-medium text-green-600 mb-2">
                  <MathRenderer text={title} />
                </h3>
              </div>
            );
          }
          
          // Handle section headings (#### Properties)
          if (trimmedLine.match(/^#### /)) {
            const title = trimmedLine.replace(/^#### /, '');
            return (
              <div key={index} className="mb-3">
                <h4 className="text-lg font-medium text-orange-600 mb-2">
                  <MathRenderer text={title} />
                </h4>
              </div>
            );
          }
          
          // Handle numbered lists (1. Introduction)
          if (trimmedLine.match(/^\d+\. /)) {
            return (
              <div key={index} className="mb-3 ml-4">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-gray-700 min-w-max">
                    {trimmedLine.match(/^\d+\./)?.[0]}
                  </span>
                  <div className="flex-1">
                    <MathRenderer text={trimmedLine.replace(/^\d+\. /, '')} />
                  </div>
                </div>
              </div>
            );
          }
          
          // Handle bullet points (- Definition)
          if (trimmedLine.match(/^[-*] /)) {
            return (
              <div key={index} className="mb-2 ml-4">
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 font-bold min-w-max mt-1">•</span>
                  <div className="flex-1">
                    <MathRenderer text={trimmedLine.replace(/^[-*] /, '')} />
                  </div>
                </div>
              </div>
            );
          }
          
          // Handle regular paragraphs
          if (trimmedLine.length > 0) {
            return (
              <div key={index} className="mb-3">
                <p className="text-gray-700 leading-relaxed">
                  <MathRenderer text={trimmedLine} />
                </p>
              </div>
            );
          }
          
          return null;
        })}
      </div>
    );
  };

  const renderFormattedLearningContent = (sections: any[]) => {
    return (
      <div className="book-content">
        {sections.map((item, i) => {
          if (item.type === 'main-heading') {
            return (
              <div key={i}>
                <h1 className="flex items-center gap-3 text-purple-600 font-bold text-2xl mb-4">
                  <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
                  {renderLearningContent(item.content)}
                </h1>
              </div>
            );
          } 
          else if (item.type === 'sub-heading') {
            return (
              <div key={i}>
                <h2 className="flex items-center gap-3 text-green-600 font-bold text-xl mb-3">
                  <div className="w-1.5 h-6 bg-gradient-to-b from-green-500 to-blue-500 rounded-full"></div>
                  {renderLearningContent(item.content)}
                </h2>
              </div>
            );
          }
          else if (item.type === 'section-heading') {
            return (
              <div key={i}>
                <h3 className="flex items-center gap-2 text-red-600 font-bold text-lg mb-2">
                  <div className="w-1 h-5 bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
                  {renderLearningContent(item.content)}
                </h3>
              </div>
            );
          }
          else if (item.type === 'section-header') {
            return (
              <div key={i} className="section-card">
                <h3 className="text-xl font-bold text-blue-600 flex items-center gap-3 mb-4">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <BookOpen className="h-6 w-6 text-indigo-600" />
                  </div>
                  {renderLearningContent(item.content)}
                </h3>
              </div>
            );
          }
          else if (item.type === 'numbered-item') {
            return (
              <div key={i} className="section-card">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-md">
                      {item.number}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-blue-800 leading-relaxed">
                      {renderLearningContent(item.content)}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          else if (item.type === 'bullet-list') {
            return (
              <div key={i} className="section-card">
                <ul className="space-y-4">
                  {item.points.map((point: string, j: number) => (
                    <li key={j} className="flex items-start gap-3 text-gray-700">
                      <div className="w-2 h-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mt-3 flex-shrink-0"></div>
                      <div className="leading-relaxed flex-1">
                        {renderLearningContent(point)}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          else if (item.type === 'code-block') {
            return (
              <div key={i} className="section-card">
                <div className="bg-gray-900 rounded-lg overflow-hidden">
                  <div className="bg-gray-800 px-4 py-2 text-sm text-gray-300">
                    {item.language}
                  </div>
                  <pre className="p-4 text-green-400 overflow-x-auto">
                    <code>{item.content}</code>
                  </pre>
                </div>
              </div>
            );
          }
          else if (item.type === 'math-content') {
            return (
              <div key={i} className="example-box">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Brackets className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-green-800 leading-relaxed space-y-2 font-mono text-base">
                      {renderLearningContent(item.content)}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          else if (item.type === 'emphasized-text') {
            return (
              <div key={i} className="highlight-box">
                <div className="text-red-800 font-medium leading-relaxed">
                  {renderLearningContent(item.content)}
                </div>
              </div>
            );
          }
          else if (item.type === 'special-note') {
            return (
              <div key={i} className="definition-box">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Info className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-blue-700 leading-relaxed space-y-2">
                      {renderLearningContent(item.content)}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          else {
            // This handles 'paragraph' type
            return (
              <div key={i}>
                <p className="text-gray-700 leading-relaxed">
                  {renderLearningContent(item.content)}
                </p>
              </div>
            );
          }
        })}
      </div>
    );
  };

  return (
    <>
      <Card className={`p-2 sm:p-4 md:p-6 dark:bg-gray-800 w-full mx-0`}>
        <div className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
            <h2 className="text-lg sm:text-xl font-bold text-[#8B5CF6] dark:text-purple-400 flex items-center gap-2">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
              Adaptive Learning Path
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openDialog('lecture')}
                className="text-xs sm:text-sm flex items-center gap-1"
              >
                <BookText className="w-3.5 h-3.5" />
                Generate Lecture
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openDialog('insights')}
                className="text-xs sm:text-sm flex items-center gap-1"
              >
                <BrainCircuit className="w-3.5 h-3.5" />
                AI Insights
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedCategories(expandedCategories.length ? [] : categories.map(c => c.id))}
                className="text-xs sm:text-sm"
              >
                {expandedCategories.length ? 'Collapse All' : 'Expand All'}
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-2 mb-4 w-full">
              <TabsTrigger value="overview">Dashboard Overview</TabsTrigger>
              <TabsTrigger value="paths">Learning Paths</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-4 w-full px-0">
              {isLoadingPerformance ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 w-full">
                  {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} className="h-24 w-full" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 w-full">
                  <StatCard 
                    title="Overall Progress" 
                    value={`${overallProgress}%`}
                    icon={<Gauge className="w-5 h-5 text-purple-500" />}
                    description="Total learning completion"
                    color="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800"
                  />
                  <StatCard 
                    title="Topics Completed" 
                    value={completedTopics}
                    icon={<Trophy className="w-5 h-5 text-green-500" />}
                    description="Out of total topics"
                    trend={5}
                    color="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                  />
                  <StatCard 
                    title="In Progress" 
                    value={inProgressTopics}
                    icon={<ArrowUpRight className="w-5 h-5 text-blue-500" />}
                    description="Topics being learned"
                    color="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                  />
                  <StatCard 
                    title="Recent Average" 
                    value={`${avgRecentScore}%`}
                    icon={<BarChart className="w-5 h-5 text-amber-500" />}
                    description="From last 5 tests"
                    trend={avgRecentScore > 65 ? 2 : -2}
                    color="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 w-full">
                <Card className="p-3 sm:p-4 border border-purple-200 dark:border-purple-800 w-full">
                  <h3 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-2 flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    Recent Performance
                  </h3>
                  {isLoadingPerformance ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : recentPerformance.length > 0 ? (
                    <div className="space-y-2">
                      {recentPerformance.map((perf, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{perf.subject}</p>
                            <p className="text-xs text-muted-foreground">{perf.date}</p>
                          </div>
                          <div className="w-24 sm:w-32">
                            <Progress value={perf.score} className="h-2" />
                          </div>
                          <div className="w-10 sm:w-12 text-right">
                            <span className={`text-sm font-medium ${
                              perf.score >= 70 ? 'text-green-500' : 
                              perf.score >= 50 ? 'text-amber-500' : 'text-red-500'
                            }`}>
                              {perf.score}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No recent performance data available</p>
                  )}
                </Card>
                
                <Card className="p-3 sm:p-4 border border-blue-200 dark:border-blue-800 w-full">
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-1.5">
                    <Star className="w-4 h-4" />
                    Recommended Topics
                  </h3>
                  {recommendedTopics.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {recommendedTopics.map(topic => (
                        <Badge key={topic} className="bg-blue-100 hover:bg-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Complete more quizzes to get recommendations</p>
                  )}
                  
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mt-4 mb-2 flex items-center gap-1.5">
                    <Brackets className="w-4 h-4" />
                    Skills to Focus On
                  </h3>
                  <div className="space-y-2">
                    {isLoadingPerformance ? (
                      <Skeleton className="h-8 w-full" />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300">
                          Problem Solving
                        </Badge>
                        <Badge variant="outline" className="border-green-400 text-green-700 dark:text-green-300">
                          Critical Thinking
                        </Badge>
                        <Badge variant="outline" className="border-purple-400 text-purple-700 dark:text-purple-300">
                          Time Management
                        </Badge>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
              
              {recommendedTopics.length > 0 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800 w-full">
                  <h3 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-2 flex items-center gap-1.5">
                    <Star className="w-4 h-4" />
                    Today's Learning Priorities:
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {recommendedTopics.slice(0, 3).map(topic => (
                      <Badge key={topic} variant="secondary" className="bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200">
                        {topic}
                      </Badge>
                    ))}
                    {recommendedTopics.length > 3 && (
                      <Badge variant="outline" className="text-purple-600 dark:text-purple-300">
                        +{recommendedTopics.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="paths" className="space-y-4 w-full px-0">
              <div className="relative mt-6 sm:mt-8">
                {categories.map((category, index) => {
                  const reviewStatus = getReviewStatus(category.id);
                  const difficulty = getRecommendedDifficulty(category);

                  return (
                    <div key={category.id} className="relative">
                      {isRecommended(category.id) && (
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 -ml-2 z-10">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="bg-yellow-400 p-0.5 rounded-full">
                                  <Star className="w-3 h-3 text-white" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left">
                                <p className="text-xs">Recommended for you based on your performance</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}

                      {reviewStatus && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 -mr-2 z-10">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className={`p-0.5 rounded-full ${
                                  reviewStatus.status === 'overdue' ? 'bg-red-500' :
                                  reviewStatus.status === 'today' ? 'bg-green-500' :
                                  'bg-blue-400'
                                }`}>
                                  {reviewStatus.status === 'overdue' ? (
                                    <AlertTriangle className="w-3 h-3 text-white" />
                                  ) : (
                                    <Clock className="w-3 h-3 text-white" />
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                <p className="text-xs">
                                  {reviewStatus.status === 'overdue' ? 'Overdue review: ' :
                                  reviewStatus.status === 'today' ? 'Review today: ' :
                                  'Scheduled review: '}
                                  {formatDate(reviewStatus.date)}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}

                      <CategoryCard
                        category={category}
                        index={index}
                        totalCategories={categories.length}
                        status={getTopicStatus(category)}
                        isExpanded={expandedCategories.includes(category.id)}
                        onToggle={() => toggleCategory(category.id)}
                        getTopicStatus={getTopicStatus}
                        recommendedDifficulty={difficulty}
                        onGenerateLecture={(topic) => handleGenerateFromSubject(topic, 'lecture')}
                        onGenerateInsights={(topic) => handleGenerateFromSubject(topic, 'insights')}
                      />
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Card>

      <Dialog open={showLectureDialog} onOpenChange={setShowLectureDialog}>
        <DialogContent className="dialog-content-large">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookText className="w-5 h-5 text-purple-600" />
              Generate Lecture
            </DialogTitle>
            {!generatedContent && (
              <DialogDescription>
                Enter a topic to generate a comprehensive lecture with key concepts and examples. Mathematical content will be properly formatted.
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="dialog-body">
            {!generatedContent && !isGenerating && (
              <div className="mb-4 px-6 pt-4">
                <label htmlFor="topic" className="text-sm font-medium mb-1 block">Topic</label>
                <div className="flex gap-2">
                  <Textarea 
                    id="topic"
                    placeholder="Enter a topic (e.g., Linear Algebra, Quantum Mechanics)"
                    value={topicInput}
                    onChange={(e) => setTopicInput(e.target.value)}
                    className="flex-1 min-h-[80px]"
                  />
                  <Button 
                    onClick={() => handleGenerateContent('lecture')} 
                    disabled={isGenerating || !topicInput.trim()}
                    className="self-start"
                  >
                    {isGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Generate
                  </Button>
                </div>
              </div>
            )}
            
            {generatedContent && (
              <div className="dialog-content-scroll">
                <div className="prose max-w-none">
                  {renderLearningContent(generatedContent)}
                </div>
              </div>
            )}

            {isGenerating && (
              <div className="flex justify-center items-center py-8">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  <p className="text-sm text-gray-500">
                    Generating comprehensive lecture with mathematical formatting...
                  </p>
                </div>
              </div>
            )}
          </div>

          {generatedContent && !isGenerating && (
            <div className="dialog-footer">
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <p className="text-sm text-gray-500 mr-auto">Download this content:</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
                  onClick={downloadAsWord}
                >
                  <FileText className="w-4 h-4" />
                  HTML (.html)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
                  onClick={downloadAsPdf}
                >
                  <Download className="w-4 h-4" />
                  Print/PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showInsightsDialog} onOpenChange={setShowInsightsDialog}>
        <DialogContent className="dialog-content-large">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BrainCircuit className="w-5 h-5 text-purple-600" />
              AI Learning Insights
            </DialogTitle>
            {!generatedContent && (
              <DialogDescription>
                Enter a topic to get AI-powered insights, learning strategies, and common pitfalls.
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="dialog-body">
            {!generatedContent && !isGenerating && (
              <div className="mb-4 px-6 pt-4">
                <label htmlFor="insight-topic" className="text-sm font-medium mb-1 block">Topic</label>
                <div className="flex gap-2">
                  <Textarea 
                    id="insight-topic" 
                    placeholder="Enter a topic you'd like insights on (e.g., Calculus, Machine Learning)"
                    value={topicInput}
                    onChange={(e) => setTopicInput(e.target.value)}
                    className="flex-1 min-h-[80px]"
                  />
                  <Button 
                    onClick={() => handleGenerateContent('insights')} 
                    disabled={isGenerating || !topicInput.trim()}
                    className="self-start"
                  >
                    {isGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Generate
                  </Button>
                </div>
              </div>
            )}
            
            {generatedContent && (
              <div className="dialog-content-scroll">
                <div className="prose max-w-none">
                  {renderLearningContent(generatedContent)}
                </div>
              </div>
            )}
            
            {isGenerating && (
              <div className="flex justify-center items-center py-8">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  <p className="text-sm text-gray-500">Generating insightful learning strategies...</p>
                </div>
              </div>
            )}
          </div>

          {generatedContent && !isGenerating && (
            <div className="dialog-footer">
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <p className="text-sm text-gray-500 mr-auto">Download this content:</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
                  onClick={downloadAsWord}
                >
                  <FileText className="w-4 h-4" />
                  HTML (.html)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
                  onClick={downloadAsPdf}
                >
                  <Download className="w-4 h-4" />
                  Print/PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LearningPathVisualization;